/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconProps } from '@fluentui/react/lib/Icon';
import { Property } from 'csstype';
import * as React from 'react';
import { usePrevious } from 'src/core/hooks/usePrevious';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { inBetween } from 'src/luis/modules/labeling/utils/generalUtils';
import { TokenSelectionStore } from 'src/luis/modules/labeling/utils/TokenSelectionStore';
import { useCalculateLineLayout } from 'src/modules/labeling/hooks/layout/useCalculateLineLayout';
import { MultilineLabel } from 'src/modules/labeling/types/Label';
import { ShapeUIProps } from 'src/modules/labeling/types/ShapeUIProps';
import { ViewOptions } from 'src/modules/labeling/types/ViewOptions';
import { CalcMaxLevelReturn, calcTokenVerticalMargin } from 'src/modules/labeling/utils/layout';
import { mapLabelToShape } from 'src/modules/labeling/utils/mapLabelToShape';
import { renderPhaseReducer } from 'src/modules/labeling/utils/renderPhaseReducer';

const { styleDiv, styleComponent } = getStylistV2('Line');

const Wrapper = styleDiv(
    'Wrapper',
    (_, { padding }) => ({
        display: 'flex',
        alignItems: 'baseline',
        position: 'relative',
        padding
    }),
    {
        cssVars: {
            padding: ''
        },
        cssVarsSetter: ({ displayGutter }: { displayGutter: boolean }) => ({
            padding: displayGutter ? '4px 0px' : '0px'
        })
    }
);

const Gutter = styleDiv('Gutter', {
    marginRight: 20
});

const Container = styleDiv(
    'Container',
    (_, cssVars) =>
        ({
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: cssVars.flexDirection as Property.FlexDirection
        } as Record<string, string>),
    {
        cssVars: {
            flexDirection: ''
        },
        cssVarsSetter: ({ isRTL }: { isRTL: boolean }) => ({
            flexDirection: isRTL ? 'row-reverse' : 'row'
        })
    }
);

const StyledIcon = styleComponent<IIconProps>(Icon)('StyledIcon', theme => ({
    color: theme.conflictIndicatorColor,
    height: theme.conflictIndicatorSize,
    width: theme.conflictIndicatorSize,
    marginLeft: 4,
    marginTop: 8
}));

export type TokenUIProps = { maxLevels: CalcMaxLevelReturn; children: React.ReactNode };

export type LineProps = {
    absoluteStartTokenIndex: number;
    lineIndex: number;
    tokens: string[];
    labels: MultilineLabel[];
    isRTL: boolean;
    hasConflict: boolean;
    viewOptions: ViewOptions;
    style?: React.CSSProperties;
    tokenSelectionStore: TokenSelectionStore;
    onCalculateHeight: (lineIndex: number, height: number) => void;
    onShapeRender: (shapeProps: ShapeUIProps, hidden: boolean) => React.ReactNode;
    onTokenRender: (tokenProps: TokenUIProps, absoluteTokenIndex: number) => React.ReactNode;
};

const getLineTokensElements = ({
    tokens,
    lineIndices,
    maxLevelHeights,
    onTokenRender,
    absoluteStartTokenIndex
}: {
    absoluteStartTokenIndex: number;
    tokens: string[];
    lineIndices: number[][];
    maxLevelHeights: CalcMaxLevelReturn[];
    onTokenRender: (tokenProps: TokenUIProps, absoluteTokenIndex: number) => React.ReactNode;
}) => {
    const elements: React.ReactNode[] = [];

    const getMaxLevelHeight = (__: string, tokenIndex: number) => {
        for (let i = 0; i < lineIndices.length; i++) {
            const [startTokenIndex, endTokenIndex] = lineIndices[i];

            if (inBetween(startTokenIndex, endTokenIndex)(tokenIndex)) {
                return maxLevelHeights[i];
            }
        }

        return { lowerMostLevel: 0, upperMostLevel: 0 };
    };

    for (let index = 0; index < tokens.length; index++) {
        const token = tokens[index];
        const absoluteTokenIndex = index + absoluteStartTokenIndex;
        elements.push(onTokenRender({ maxLevels: getMaxLevelHeight(null, absoluteTokenIndex), children: token }, absoluteTokenIndex));
    }

    return elements;
};

/**
 * Component used to render labeler lines and
 * do layout calculations.
 */
export const Line = React.memo((props: LineProps) => {
    const {
        isRTL,
        absoluteStartTokenIndex,
        onShapeRender,
        onTokenRender,
        tokens,
        labels,
        lineIndex,
        style,
        hasConflict,
        onCalculateHeight,
        tokenSelectionStore,
        viewOptions: { hideNames, displayGutter }
    } = props;
    const lineRef = React.useRef<HTMLDivElement>();
    const [renderPhase, dispatchRenderPhase] = React.useReducer(renderPhaseReducer, 'calculateInitialTokenBounds');
    const prevRenderPhase = usePrevious(renderPhase);

    const displayShapes = (prevRenderPhase === 'ready' && renderPhase === 'calculatePaddedTokenBounds') || renderPhase === 'ready';

    const { lineIndices, maxLevelHeights, multilineLabels, tokenRects } = useCalculateLineLayout({
        absoluteStartTokenIndex,
        dispatchRenderPhase,
        hideNames,
        labels,
        renderPhase,
        tokens,
        containerRef: lineRef
    });

    const shapesProps = React.useMemo(() => multilineLabels.map(mapLabelToShape(tokenRects, isRTL, absoluteStartTokenIndex)), [
        multilineLabels,
        tokenRects,
        absoluteStartTokenIndex,
        isRTL
    ]);

    const linesTokensElements = React.useMemo(
        () => getLineTokensElements({ lineIndices, maxLevelHeights, onTokenRender, tokens, absoluteStartTokenIndex }),
        [lineIndices, maxLevelHeights, onTokenRender, tokens]
    );

    const onLineClick = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if ((event.target as HTMLElement) === lineRef.current) {
                tokenSelectionStore.cancel();
            }
        },
        [lineRef.current]
    );

    React.useEffect(() => {
        if (renderPhase === 'ready') {
            onCalculateHeight(lineIndex, lineRef.current.clientHeight);
        }
    }, [renderPhase]);

    return (
        <Wrapper originalRef={lineRef} style={style} customProps={{ displayGutter }} onClick={onLineClick}>
            {displayGutter && <Gutter>{lineIndex + 1}</Gutter>}

            <Container customProps={{ isRTL }}>{linesTokensElements}</Container>

            {shapesProps.map(shapeProp => onShapeRender(shapeProp, !displayShapes))}

            {lineIndex === 0 && hasConflict && (
                <StyledIcon
                    iconName="WarningSolid"
                    style={{
                        marginTop: calcTokenVerticalMargin(maxLevelHeights[0]?.upperMostLevel ?? 0, hideNames)
                    }}
                />
            )}
        </Wrapper>
    );
});
